import { Component, h, State, Prop, Element, Listen, Event, EventEmitter } from '@stencil/core';

const MCP_BASE_URL = 'https://mcp.biel.ai/v2';

@Component({
  tag: 'biel-input-options',
  styleUrl: 'biel-input-options.css',
  shadow: true,
})
export class BielInputOptions {
  @Prop() project: string = '';
  @Prop() mcpEnabled: boolean = false;
  @Prop() mcpServerName: string = '';
  @Prop() mcpMetadataTag: string = '';
  @Prop() mcpServerUrl: string = '';
  @Prop() apiKey: string = '';
  @Prop() hideSettingsButton: boolean = false;
  @Prop() hideConnectButton: boolean = false;
  @Prop() currentThinkMode: 'auto' | 'fast' | 'think' = 'auto';
  @Prop() bielId: string;

  // Configuration props for text items
  @Prop() connectButtonText: string = 'Connect';
  @Prop() thinkModeAutoText: string = 'Auto';
  @Prop() thinkModeFastText: string = 'Instant';
  @Prop() thinkModeThinkText: string = 'Think';
  @Prop() thinkModeAutoDescription: string = 'Balances speed and quality';
  @Prop() thinkModeFastDescription: string = 'Prioritizes speed';
  @Prop() thinkModeThinkDescription: string = 'Takes longer for better accuracy';
  @Prop() mcpUrlText: string = 'MCP URL';
  @Prop() mcpUrlDescription: string = 'Copy to clipboard';
  @Prop() mcpClaudeText: string = 'Claude Code';
  @Prop() mcpClaudeDescription: string = 'CLI command';
  @Prop() mcpCopilotText: string = 'Copilot (VS code)';
  @Prop() mcpCopilotDescription: string = 'Opens in a new tab';
  @Prop() mcpCursorText: string = 'Cursor';
  @Prop() mcpCursorDescription: string = 'Opens in a new tab';
  @Prop() mcpCopiedText: string = 'Copied!';
  @Prop() mcpUrlCopiedDescription: string = 'URL copied to clipboard';
  @Prop() mcpClaudeCopiedDescription: string = 'Command copied to clipboard';

  @State() connectDropdownOpen: boolean = false;
  @State() settingsDropdownOpen: boolean = false;
  @State() showCopySuccess: boolean = false;
  @State() showClaudeCopySuccess: boolean = false;
  @State() selectedThinkMode: 'auto' | 'fast' | 'think' = 'auto';

  @Element() el: HTMLElement;

  @Event() thinkModeChanged: EventEmitter<{ bielId: string; mode: 'auto' | 'fast' | 'think' }>;

  componentWillLoad() {
    this.selectedThinkMode = this.currentThinkMode;
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.connectDropdownOpen = false;
      this.settingsDropdownOpen = false;
    }
  }

  toggleConnectDropdown() {
    this.connectDropdownOpen = !this.connectDropdownOpen;
    if (this.connectDropdownOpen) {
      this.settingsDropdownOpen = false;
    }
  }

  toggleSettingsDropdown() {
    this.settingsDropdownOpen = !this.settingsDropdownOpen;
    if (this.settingsDropdownOpen) {
      this.connectDropdownOpen = false;
    }
  }

  handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    // Check for Connect dropdown elements
    const connectDropdown = target.closest('biel-input-option[data-dropdown="connect"]');

    // Check for Settings dropdown elements
    const settingsDropdown = target.closest('biel-input-option[data-dropdown="settings"]');

    // Close Connect dropdown if clicked outside
    if (!connectDropdown && this.connectDropdownOpen) {
      this.connectDropdownOpen = false;
    }

    // Close Settings dropdown if clicked outside
    if (!settingsDropdown && this.settingsDropdownOpen) {
      this.settingsDropdownOpen = false;
    }
  };

  componentDidLoad() {
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleClickOutside);
  }

  getMcpServerUrl(): string {
    // If a custom MCP server URL is provided, use it directly
    if (this.mcpServerUrl) {
      return this.mcpServerUrl;
    }

    // Otherwise, construct the URL automatically
    const baseUrl = `${MCP_BASE_URL}/${this.project}/mcp`;
    const params = new URLSearchParams();

    if (this.mcpMetadataTag) {
      params.append('metadata', this.mcpMetadataTag);
    }

    if (this.apiKey) {
      params.append('api_key', this.apiKey);
    }

    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  async copyMcpUrl(event?: Event) {
    event?.stopPropagation();
    const url = this.getMcpServerUrl();

    try {
      await navigator.clipboard.writeText(url);
      this.showCopySuccess = true;
      setTimeout(() => {
        this.showCopySuccess = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }

  async copyClaudeUrl(event?: Event) {
    event?.stopPropagation();
    const url = this.getMcpServerUrl();
    const claudeUrl = `claude mcp add ${this.mcpServerName} --transport http "${url}"`;

    try {
      await navigator.clipboard.writeText(claudeUrl);
      this.showClaudeCopySuccess = true;
      setTimeout(() => {
        this.showClaudeCopySuccess = false;
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }

  connectToIde(ide: 'copilot' | 'cursor', event?: Event) {
    event?.stopPropagation();
    const mcpUrl = this.getMcpServerUrl();

    const cursorConfig = JSON.stringify({
      type: 'http',
      url: `${mcpUrl}`,
    });
    const cursorConfigBase64 = btoa(cursorConfig);

    const vscodeUrl = JSON.stringify({
      name: `${this.mcpServerName}`,
      type: 'http',
      url: `${mcpUrl}`,
    });

    const urls = {
      copilot: `vscode:mcp/install?${vscodeUrl}`,
      cursor: `cursor://anysphere.cursor-deeplink/mcp/install?name=${this.mcpServerName}&config=${cursorConfigBase64}`,
    };

    window.open(urls[ide], '_blank');
    this.connectDropdownOpen = false;
  }

  handleThinkModeSelect(mode: 'auto' | 'fast' | 'think', event?: Event) {
    event?.stopPropagation();
    this.selectedThinkMode = mode;
    this.settingsDropdownOpen = false;
    this.thinkModeChanged.emit({ bielId: this.bielId, mode });
  }

  render() {
    const shouldShow = this.mcpEnabled || !this.hideSettingsButton;

    if (!shouldShow) {
      return null;
    }

    return (
      <div class="biel-input-options-container" part="container">
        {/* Settings Dropdown*/}
        {!this.hideSettingsButton && (
          <biel-input-option
            data-dropdown="settings"
            isOpen={this.settingsDropdownOpen}
            label={
              this.selectedThinkMode === 'auto'
                ? this.thinkModeAutoText
                : this.selectedThinkMode === 'fast'
                  ? this.thinkModeFastText
                  : this.thinkModeThinkText
            }
            onToggleRequested={() => this.toggleSettingsDropdown()}
            exportparts="button: settings-button, dropdown: settings-dropdown"
          >
            <div slot="dropdown-content" class="biel-input-options__dropdown-content">
              <button
                class={`biel-input-options__dropdown-item ${this.selectedThinkMode === 'auto' ? 'biel-input-options__dropdown-item--selected' : ''}`}
                onClick={(e) => this.handleThinkModeSelect('auto', e)}
              >
                {this.selectedThinkMode === 'auto' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="biel-input-options__dropdown-checkmark"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
                <span class="biel-input-options__dropdown-title">{this.thinkModeAutoText}</span>
                <span class="biel-input-options__dropdown-description">
                  {this.thinkModeAutoDescription}
                </span>
              </button>
              <button
                class={`biel-input-options__dropdown-item ${this.selectedThinkMode === 'fast' ? 'biel-input-options__dropdown-item--selected' : ''}`}
                onClick={(e) => this.handleThinkModeSelect('fast', e)}
              >
                {this.selectedThinkMode === 'fast' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="biel-input-options__dropdown-checkmark"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
                <span class="biel-input-options__dropdown-title">{this.thinkModeFastText}</span>
                <span class="biel-input-options__dropdown-description">
                  {this.thinkModeFastDescription}
                </span>
              </button>
              <button
                class={`biel-input-options__dropdown-item ${this.selectedThinkMode === 'think' ? 'biel-input-options__dropdown-item--selected' : ''}`}
                onClick={(e) => this.handleThinkModeSelect('think', e)}
              >
                {this.selectedThinkMode === 'think' && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="biel-input-options__dropdown-checkmark"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                )}
                <span class="biel-input-options__dropdown-title">{this.thinkModeThinkText}</span>
                <span class="biel-input-options__dropdown-description">
                  {this.thinkModeThinkDescription}
                </span>
              </button>
            </div>
          </biel-input-option>
        )}

        {/* Connect Dropdown */}
        {this.mcpEnabled && !this.hideConnectButton && (
          <biel-input-option
            data-dropdown="connect"
            isOpen={this.connectDropdownOpen}
            label={this.connectButtonText}
            onToggleRequested={() => this.toggleConnectDropdown()}
            exportparts="button: connect-button, dropdown: connect-dropdown"
          >
            <div slot="dropdown-content" class="biel-input-options__dropdown-content">
              <button
                class="biel-input-options__dropdown-item"
                onClick={(e) => this.copyMcpUrl(e)}
                role="menuitem"
              >
                <span class="biel-input-options__dropdown-title">
                  {this.showCopySuccess ? this.mcpCopiedText : this.mcpUrlText}
                </span>
                <span class="biel-input-options__dropdown-description">
                  {this.showCopySuccess ? this.mcpUrlCopiedDescription : this.mcpUrlDescription}
                </span>
              </button>

              <button
                class="biel-input-options__dropdown-item"
                onClick={(e) => this.copyClaudeUrl(e)}
                role="menuitem"
              >
                <span class="biel-input-options__dropdown-title">
                  {this.showClaudeCopySuccess ? this.mcpCopiedText : this.mcpClaudeText}
                </span>
                <span class="biel-input-options__dropdown-description">
                  {this.showClaudeCopySuccess
                    ? this.mcpClaudeCopiedDescription
                    : this.mcpClaudeDescription}
                </span>
              </button>

              <button
                class="biel-input-options__dropdown-item"
                onClick={(e) => this.connectToIde('copilot', e)}
                role="menuitem"
              >
                <span class="biel-input-options__dropdown-title">{this.mcpCopilotText}</span>
                <span class="biel-input-options__dropdown-description">
                  {this.mcpCopilotDescription}
                </span>
              </button>

              <button
                class="biel-input-options__dropdown-item"
                onClick={(e) => this.connectToIde('cursor', e)}
                role="menuitem"
              >
                <span class="biel-input-options__dropdown-title">{this.mcpCursorText}</span>
                <span class="biel-input-options__dropdown-description">
                  {this.mcpCursorDescription}
                </span>
              </button>
            </div>
          </biel-input-option>
        )}
      </div>
    );
  }
}
